import {
  easeInOutSine,
  smoothstep,
  clamp,
  w, h,
  frame,
  frameN,
  setFrameN,
  getCanvasSize,
  setupCommon,
  drawCommon,
  make,
} from '../js/common.js';

let img;
const imgUrl = new URL('./animals.png', import.meta.url);
export function preload() {
	img = loadImage(imgUrl.href);
}

let palette;
let skew;
export function setup() {
	setupCommon();

	palette = convertRGBtoHSL(getPalette())
		.filter(c => c.l > 20);

	skew = Math.floor(noise(PI) * 10);
	console.log(skew);

	background(20, 40, 10);
}

// https://github.com/zygisS22/color-palette-extraction/blob/master/index.js
// MIT License
// Copyright (c) 2022 Zygimantas Sniurevicius
function getPalette() {
	img.loadPixels();
	const rgbArray = [];
	for (let i = 0; i < img.pixels.length; i += 4) {
		const rgb = {
			r: img.pixels[i],
			g: img.pixels[i + 1],
			b: img.pixels[i + 2],
		};
		rgbArray.push(rgb);
	}

	const findBiggestColorRange = (rgbValues) => {
		let rMin = Number.MAX_VALUE;
		let gMin = Number.MAX_VALUE;
		let bMin = Number.MAX_VALUE;

		let rMax = Number.MIN_VALUE;
		let gMax = Number.MIN_VALUE;
		let bMax = Number.MIN_VALUE;

		rgbValues.forEach((pixel) => {
			rMin = Math.min(rMin, pixel.r);
			gMin = Math.min(gMin, pixel.g);
			bMin = Math.min(bMin, pixel.b);

			rMax = Math.max(rMax, pixel.r);
			gMax = Math.max(gMax, pixel.g);
			bMax = Math.max(bMax, pixel.b);
		});

		const rRange = rMax - rMin;
		const gRange = gMax - gMin;
		const bRange = bMax - bMin;

		const biggestRange = Math.max(rRange, gRange, bRange);
		if (biggestRange === rRange) {
			return "r";
		} else if (biggestRange === gRange) {
			return "g";
		} else {
			return "b";
		}
	};

	const quantization = (rgbValues, depth) => {
		const MAX_DEPTH = 4;

		// Base case
		if (depth === MAX_DEPTH || rgbValues.length === 0) {
			const color = rgbValues.reduce(
				(prev, curr) => {
					prev.r += curr.r;
					prev.g += curr.g;
					prev.b += curr.b;

					return prev;
				},
				{
					r: 0,
					g: 0,
					b: 0,
				}
			);

			color.r = Math.round(color.r / rgbValues.length);
			color.g = Math.round(color.g / rgbValues.length);
			color.b = Math.round(color.b / rgbValues.length);

			return [color];
		};

		/**
	 *  Recursively do the following:
	 *  1. Find the pixel channel (red,green or blue) with biggest difference/range
	 *  2. Order by this channel
	 *  3. Divide in half the rgb colors list
	 *  4. Repeat process again, until desired depth or base case
	 */
		const componentToSortBy = findBiggestColorRange(rgbValues);
		rgbValues.sort((p1, p2) => {
			return p1[componentToSortBy] - p2[componentToSortBy];
		});

		const mid = rgbValues.length / 2;
		return [
			...quantization(rgbValues.slice(0, mid), depth + 1),
			...quantization(rgbValues.slice(mid + 1), depth + 1),
		];
	};

	const quantColors = quantization(rgbArray, 0);
	return quantColors;
}

// https://github.com/zygisS22/color-palette-extraction/blob/master/index.js
// MIT License
// Copyright (c) 2022 Zygimantas Sniurevicius
function convertRGBtoHSL(rgbValues) {
	/**
	 * Convert RGB values to HSL
	 * This formula can be
	 * found here https://www.niwa.nu/2013/05/math-behind-colorspace-conversions-rgb-hsl/
	 */
	return rgbValues.map((pixel) => {
		let hue,
			saturation,
			luminance = 0;

		// first change range from 0-255 to 0 - 1
		let redOpposite = pixel.r / 255;
		let greenOpposite = pixel.g / 255;
		let blueOpposite = pixel.b / 255;

		const Cmax = Math.max(redOpposite, greenOpposite, blueOpposite);
		const Cmin = Math.min(redOpposite, greenOpposite, blueOpposite);

		const difference = Cmax - Cmin;

		luminance = (Cmax + Cmin) / 2.0;

		if (luminance <= 0.5) {
			saturation = difference / (Cmax + Cmin);
		} else if (luminance >= 0.5) {
			saturation = difference / (2.0 - Cmax - Cmin);
		}

		/**
		 * If Red is max, then Hue = (G-B)/(max-min)
		 * If Green is max, then Hue = 2.0 + (B-R)/(max-min)
		 * If Blue is max, then Hue = 4.0 + (R-G)/(max-min)
		 */
		const maxColorValue = Math.max(pixel.r, pixel.g, pixel.b);

		if (maxColorValue === pixel.r) {
			hue = (greenOpposite - blueOpposite) / difference;
		} else if (maxColorValue === pixel.g) {
			hue = 2.0 + (blueOpposite - redOpposite) / difference;
		} else {
			hue = 4.0 + (greenOpposite - blueOpposite) / difference;
		}

		hue = hue * 60; // find the sector of 60 degrees to which the color belongs

		// it should be always a positive angle
		if (hue < 0) {
			hue = hue + 360;
		}

		// When all three of R, G and B are equal, we get a neutral color: white, grey or black.
		if (difference === 0) {
			return false;
		}

		return {
			h: Math.round(hue) + 180, // plus 180 degrees because that is the complementary color
			s: parseFloat(saturation * 100).toFixed(2),
			l: parseFloat(luminance * 100).toFixed(2),
		};
	});
}

const num = 80;
export function draw() {
	drawCommon();
	background(100);

	noStroke();
	for (let x0 = 0; x0 < 10; x0++) {
		for (let y0 = 0; y0 < 10; y0++) {
			const c = palette[(x0 + y0 + skew * y0) % palette.length];
			fill(...[c.h, c.s, c.l]);
			rect(w(x0 / 10), h(y0 / 10), w(1 / 10), h(1 / 10));
		}
	}

	const frameN = (frameCount % 600) / 600;
	for (let x0 = 0; x0 < num; x0++) {
		for (let y0 = 0; y0 < num; y0++) {
			const n = noise(x0 * 2.1, y0 * 3.05) * 2 - 1;
			const x = (x0 + 0.5) / num + n * 0.01;
			const y = (y0 + 0.5) / num + n * 0.01;

			const r0 = 0.0065;

			const n1 = dist(x, y, 0.5, 0.5) * PI;
			const ny = dist(x, y,
				0.5 + 0.2 * Math.cos(TAU * frameN * 3 + QUARTER_PI + n1),
				0.5 + 0.2 * Math.sin(TAU * frameN * 3 + QUARTER_PI + n1)) * 1 * TAU;
			const r = r0 + 0.002 * Math.sin(frameN * -5 * TAU + ny * 3);

			const c0 = Math.floor(palette.length * noise(x, y) * 150) % palette.length;
			const c = palette[c0];
			fill(...[c.h, c.s, c.l]);
			ellipse(w(x), h(y), w(r));
		}
	}
}
